import { Box, Grid } from '@mui/material';
import { sliderClasses } from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import React, { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import ChartCard from 'components/charts/chart-card';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import SparklineChart from 'components/charts/sparkline-chart';
import Slider from 'components/slider';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import CategoryBarChart from 'views/playground/features/data-lab/components/charts/category-bar-chart';
import ConditionRadarChart from 'views/playground/features/data-lab/components/charts/condition-radar-chart';
import ConversionFunnelChart from 'views/playground/features/data-lab/components/charts/conversion-funnel-chart';
import CorrelationScatterChart from 'views/playground/features/data-lab/components/charts/correlation-scatter-chart';
import DensityHeatmap from 'views/playground/features/data-lab/components/charts/density-heatmap';
import DistributionDonutChart from 'views/playground/features/data-lab/components/charts/distribution-donut-chart';
import NestedDonutChart from 'views/playground/features/data-lab/components/charts/nested-donut-chart';
import SeverityBarChart from 'views/playground/features/data-lab/components/charts/severity-bar-chart';
import TrendLineChart from 'views/playground/features/data-lab/components/charts/trend-line-chart';
import UtilizationGauge from 'views/playground/features/data-lab/components/charts/utilization-gauge';
import DataLab from 'views/playground/features/data-lab/components/data-lab';
import { CategoryPoint } from 'views/playground/features/data-lab/types/chart-series.type';
import { UTILIZATION_THRESHOLDS } from 'views/playground/features/data-lab/utils/health-status';
import {
  deriveCategoryBarData,
  deriveConditionRadarData,
  deriveCorrelationScatterData,
  deriveDistributionDonutData,
  deriveMiniSparklineData,
  deriveNestedDonutData,
  deriveSeverityBarData,
  deriveTrendLineData,
  deriveUtilizationGaugeData,
} from 'views/playground/features/data-lab/utils/derive-chart-data';

// Derived once at module scope from the fixture, same as the fixture itself.
const trendLineData = deriveTrendLineData();
const categoryBarData = deriveCategoryBarData();
const severityBarData = deriveSeverityBarData();
const nestedDonutData = deriveNestedDonutData();
const correlationScatterData = deriveCorrelationScatterData();
const conditionRadarData = deriveConditionRadarData();
const utilizationGaugeData = deriveUtilizationGaugeData();
const miniSparklineData = deriveMiniSparklineData();

type UtilizationControlValues = {
  value: number;
};

const UtilizationSliderContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  color: chartPalette.text.secondary,
  '& .MuiTypography-root': {
    color: chartPalette.text.secondary,
  },
  [`& .${sliderClasses.root}`]: {
    color: chartPalette.series.categorical[0],
    [`& .${sliderClasses.rail}`]: {
      backgroundColor: chartPalette.borders.strong,
      opacity: 1,
    },
    [`& .${sliderClasses.track}`]: {
      backgroundColor: chartPalette.series.categorical[0],
      border: 0,
    },
    [`& .${sliderClasses.thumb}`]: {
      width: 16,
      height: 16,
      margin: 0,
      transform: 'translate(-50%, -50%)',
      backgroundColor: chartPalette.backgrounds.surface,
      border: `2px solid ${chartPalette.series.categorical[0]}`,
      '&:hover, &.Mui-focusVisible, &.Mui-active': {
        boxShadow: `0 0 0 5px color-mix(in srgb, ${chartPalette.series.categorical[0]} 25%, transparent)`,
      },
    },
    [`& .${sliderClasses.mark}`]: {
      height: 8,
      width: 2,
      backgroundColor: chartPalette.text.secondary,
    },
    [`& .${sliderClasses.markLabel}`]: {
      color: chartPalette.text.secondary,
    },
    [`& .${sliderClasses.valueLabel}`]: {
      display: 'none',
    },
  },
}));

// Each chart gets its own card so the controls preview the full chart set.
export const GalleryScreenBody: React.FC = () => {
  const distributionDonutData = useMemo(() => deriveDistributionDonutData('Other'), []);
  const palette = useChartPalette();
  const { control } = useForm<UtilizationControlValues>({
    defaultValues: { value: utilizationGaugeData.value },
  });
  const utilizationValue = useWatch({ control, name: 'value' });

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [selectedNested, setSelectedNested] = useState<string | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<string | null>(null);
  const formatUtilizationValue = (value: number) => `${value}%`;

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Trend line" subtitle="Line chart · area gradient with legend toggling">
          <TrendLineChart data={trendLineData} />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Category bar" subtitle="Bar chart · grouped series with legend toggling">
          <CategoryBarChart data={categoryBarData} selectedId={selectedCategory} onSelect={setSelectedCategory} />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Severity bar" subtitle="Bar chart · status colour by threshold">
          <SeverityBarChart data={severityBarData} selectedId={selectedSeverity} onSelect={setSelectedSeverity} />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Distribution donut" subtitle="Donut chart · hover swaps the centre label">
          <DistributionDonutChart data={distributionDonutData} selectedId={selectedSite} onSelect={setSelectedSite} />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Nested donut" subtitle="Nested donut · legend groups sites by biomass band">
          <NestedDonutChart data={nestedDonutData} selectedId={selectedNested} onSelect={setSelectedNested} />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Correlation scatter" subtitle="Scatter chart · item highlight on hover">
          <CorrelationScatterChart data={correlationScatterData} selectedId={selectedPoint} onSelect={setSelectedPoint} />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Condition radar" subtitle="Radar chart · two overlaid profiles">
          <ConditionRadarChart data={conditionRadarData} />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Utilization gauge" subtitle="Gauge · status colour by threshold">
          <Box display="flex" flexDirection="column" minHeight={320} color={palette.text.secondary}>
            <UtilizationGauge data={{ ...utilizationGaugeData, value: utilizationValue }} />
            <UtilizationSliderContainer chartPalette={palette}>
              <Slider
                label="Adjust utilization"
                controller={{ name: 'value', control }}
                min={0}
                max={utilizationGaugeData.max}
                step={1}
                marks={[
                  { value: UTILIZATION_THRESHOLDS.watch, label: formatUtilizationValue(UTILIZATION_THRESHOLDS.watch) },
                  { value: UTILIZATION_THRESHOLDS.critical, label: formatUtilizationValue(UTILIZATION_THRESHOLDS.critical) },
                ]}
                getAriaValueText={formatUtilizationValue}
              />
            </UtilizationSliderContainer>
          </Box>
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Density heatmap" subtitle="Out of scope (license restricted) · needs Heatmap from Pro">
          <DensityHeatmap />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Conversion funnel" subtitle="Out of scope (license restricted) · needs FunnelChart from Pro">
          <ConversionFunnelChart />
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ChartCard title="Mini sparkline" subtitle="Sparkline · decoration for KPI cards and table cells" height={120}>
          <SparklineChart data={miniSparklineData} height={80} />
        </ChartCard>
      </Grid>
    </Grid>
  );
};

const GalleryScreen: React.FC = () => (
  <DataLab>
    <GalleryScreenBody />
  </DataLab>
);

export default GalleryScreen;
